"use client";

import Image from "next/image";
import type { ComponentType } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownToLine,
  Check,
  ChevronRight,
  Download,
  Ellipsis,
  PlusSquare,
  Share2,
  Smartphone,
  Sparkles,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type BeforeInstallPromptEvent,
  detectInstallPlatform,
  formatIosVersionLabel,
  isStandaloneDisplayMode,
  type IosInstructionVariant,
} from "@/lib/pwa/install";
import { cn } from "@/lib/utils";

const NEVER_SHOW_KEY = "vvm_pwa_install_prompt_never";
const SESSION_CLOSE_KEY = "vvm_pwa_install_prompt_closed";

interface PwaInstallPromptProps {
  enabled?: boolean;
}

interface StepItem {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

function readLocalFlag(key: string) {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(key) === "1";
}

function writeLocalFlag(key: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(key, "1");
}

function readSessionFlag(key: string) {
  if (typeof window === "undefined") {
    return false;
  }

  return window.sessionStorage.getItem(key) === "1";
}

function writeSessionFlag(key: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(key, "1");
}

function getIosInstallSteps(variant: IosInstructionVariant | null, isSafari: boolean): StepItem[] {
  if (!isSafari) {
    return [
      {
        icon: Smartphone,
        title: "Abri esta pagina en Safari",
        description: "En iPhone o iPad, la instalacion solo se puede hacer desde Safari.",
      },
      {
        icon: Ellipsis,
        title: "Toca los 3 puntitos de abajo",
        description: "Primero abri el menu del navegador para que te aparezca la opcion Compartir.",
      },
      {
        icon: Share2,
        title: "Toca Compartir",
        description: "Busca el boton Compartir de Safari en la barra inferior o superior.",
      },
      {
        icon: PlusSquare,
        title: "Elegi Agregar a pantalla de inicio",
        description: "Si no aparece, baja hasta el final y agregalo desde Editar acciones.",
      },
      {
        icon: Check,
        title: "Confirma con Agregar",
        description: "La app va a quedar instalada en tu pantalla de inicio.",
      },
    ];
  }

  if (variant === "ios18_plus") {
    return [
      {
        icon: Ellipsis,
        title: "Toca los 3 puntitos de abajo",
        description: "Primero abri el menu del navegador para que te aparezca la opcion Compartir.",
      },
      {
        icon: Share2,
        title: "Toca Compartir",
        description:
          "Usa el boton Compartir de Safari. Segun el layout, puede estar arriba o abajo.",
      },
      {
        icon: PlusSquare,
        title: "Elegi Agregar a pantalla de inicio",
        description: "Si no lo ves, entra a Editar acciones y agregalo al menu.",
      },
      {
        icon: ArrowDownToLine,
        title: "Deja activo Abrir como app web",
        description: "Eso hace que VM APP abra como app, sin la barra del navegador.",
      },
      {
        icon: Check,
        title: "Toca Agregar",
        description: "Queda instalada en el inicio y entra directo como app.",
      },
    ];
  }

  if (variant === "ios17" || variant === "ios16_4_to_16_7") {
    return [
      {
        icon: Ellipsis,
        title: "Toca los 3 puntitos de abajo",
        description: "Primero abri el menu del navegador para que te aparezca la opcion Compartir.",
      },
      {
        icon: Share2,
        title: "Toca Compartir",
        description: "El boton puede estar abajo o arriba, segun como tengas configurado Safari.",
      },
      {
        icon: PlusSquare,
        title: "Elegi Agregar a pantalla de inicio",
        description: "Si no aparece, baja al final del menu y sumalo desde Editar acciones.",
      },
      {
        icon: ArrowDownToLine,
        title: "Si aparece Abrir como app web, dejalo activo",
        description: "Asi VM APP se abre con aspecto de app instalada.",
      },
      {
        icon: Check,
        title: "Confirma con Agregar",
        description: "Vas a verla en el inicio como si fuera una app.",
      },
    ];
  }

  return [
    {
      icon: Ellipsis,
      title: "Toca los 3 puntitos de abajo",
      description: "Primero abri el menu del navegador para que te aparezca Compartir.",
    },
    {
      icon: Share2,
      title: "Toca Compartir",
      description: "En Safari, el boton Compartir puede estar abajo o arriba segun tu version.",
    },
    {
      icon: PlusSquare,
      title: "Elegi Agregar a pantalla de inicio",
      description: "En versiones anteriores no suele aparecer la opcion de app web.",
    },
    {
      icon: Check,
      title: "Confirma con Agregar",
      description: "La app va a quedar accesible desde el inicio del iPhone.",
    },
  ];
}

export function PwaInstallPrompt({ enabled = true }: PwaInstallPromptProps) {
  const [open, setOpen] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installing, setInstalling] = useState(false);
  const [platformInfo, setPlatformInfo] = useState(() => detectInstallPlatform("", false));

  const iosSteps = useMemo(
    () => getIosInstallSteps(platformInfo.iosInstructionVariant, platformInfo.isSafari),
    [platformInfo.iosInstructionVariant, platformInfo.isSafari],
  );

  const canShowPrompt = useCallback(() => {
    if (!enabled) {
      return false;
    }

    if (readLocalFlag(NEVER_SHOW_KEY) || readSessionFlag(SESSION_CLOSE_KEY)) {
      return false;
    }

    return true;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    const currentPlatform = detectInstallPlatform(navigator.userAgent, isStandaloneDisplayMode());
    setPlatformInfo(currentPlatform);

    if (currentPlatform.isStandalone || !canShowPrompt()) {
      return;
    }

    if (currentPlatform.platform === "ios") {
      setOpen(true);
      return;
    }

    if (currentPlatform.platform !== "android") {
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setOpen(true);
    };

    const handleAppInstalled = () => {
      setInstallEvent(null);
      setOpen(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [canShowPrompt, enabled]);

  const handleClose = (persistForSession: boolean) => {
    if (persistForSession) {
      writeSessionFlag(SESSION_CLOSE_KEY);
    }
    setOpen(false);
  };

  const handleNeverShow = () => {
    writeLocalFlag(NEVER_SHOW_KEY);
    setOpen(false);
  };

  const handleInstall = async () => {
    if (platformInfo.platform === "ios") {
      if (showIosGuide) {
        handleClose(true);
        return;
      }
      setShowIosGuide(true);
      return;
    }

    if (!installEvent) {
      return;
    }

    setInstalling(true);
    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice.outcome === "accepted") {
        setOpen(false);
      }
      setInstallEvent(null);
    } finally {
      setInstalling(false);
    }
  };

  const installButtonDisabled = platformInfo.platform === "android" && installEvent == null;
  const versionLabel = formatIosVersionLabel(
    platformInfo.iosVersionMajor,
    platformInfo.iosVersionMinor,
    platformInfo.iosInstructionVariant,
  );

  const recommendationText =
    platformInfo.platform === "ios"
      ? "Instalala en el inicio para que abra como app y quede mucho mas directo usarla."
      : "Instalala para abrir mas rapido, usar pantalla completa y sentirla como app real.";

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? setOpen(true) : handleClose(true))}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[92vh] w-[min(calc(100vw-1.5rem),34rem)] flex-col overflow-hidden rounded-[28px] border border-primary/30 bg-[#06141a] p-0 text-foreground shadow-[0_30px_90px_rgba(0,0,0,0.65)]"
      >
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(0,224,216,0.18),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(0,145,255,0.12),transparent_38%)]" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent" />

          <div className="relative flex max-h-[92vh] flex-col">
            <div className="space-y-6 overflow-y-auto overscroll-contain p-5 sm:p-6">
              <DialogHeader className="space-y-4 text-left">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/25 bg-white/5 shadow-[0_0_30px_rgba(0,224,216,0.12)]">
                      <Image
                        src="/images/logo.png"
                        alt="Vivi el Movimiento"
                        width={36}
                        height={36}
                        className="h-9 w-9 object-contain"
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        Instalar VM APP
                      </div>
                      <DialogTitle className="text-2xl font-extrabold tracking-tight text-white">
                        {showIosGuide ? "Instalacion en iPhone" : "Usala como app"}
                      </DialogTitle>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 rounded-full border border-white/10 text-white/70 hover:bg-white/10 hover:text-white"
                    onClick={() => handleClose(true)}
                    aria-label="Cerrar recomendacion de instalacion"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>

                <DialogDescription className="max-w-[34ch] text-sm leading-6 text-white/72">
                  {showIosGuide ? (
                    <>
                      {platformInfo.isSafari
                        ? "En iPhone no podemos abrir el instalador automaticamente, pero te dejamos el camino exacto segun tu version."
                        : "En iPhone la instalacion solo se puede hacer desde Safari. Te mostramos el camino mas corto para hacerlo."}
                    </>
                  ) : (
                    recommendationText
                  )}
                </DialogDescription>
              </DialogHeader>

              {!showIosGuide ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80">
                      Acceso
                    </p>
                    <p className="mt-2 text-sm font-semibold text-white">
                      Entra directo desde el inicio
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80">
                      Pantalla completa
                    </p>
                    <p className="mt-2 text-sm font-semibold text-white">
                      Menos ruido del navegador
                    </p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80">
                      Experiencia
                    </p>
                    <p className="mt-2 text-sm font-semibold text-white">Se siente mucho mas app</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      {versionLabel}
                    </span>
                    <span
                      className={cn(
                        "rounded-full border px-3 py-1 text-xs font-semibold",
                        platformInfo.isSafari
                          ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                          : "border-amber-400/30 bg-amber-400/10 text-amber-300",
                      )}
                    >
                      {platformInfo.isSafari ? "Safari" : "Abrir en Safari"}
                    </span>
                  </div>

                  <div className="grid gap-3">
                    {iosSteps.map((step, index) => (
                      <div
                        key={`${step.title}-${index}`}
                        className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.035] p-4"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                          <step.icon className="h-4.5 w-4.5" aria-hidden="true" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-bold text-white">
                            {index + 1}. {step.title}
                          </p>
                          <p className="text-sm leading-5 text-white/70">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs leading-5 text-white/50">
                    Referencia usada: guia actual de Apple para agregar sitios a la pantalla de
                    inicio y abrirlos como app web en iPhone.
                  </p>
                </div>
              )}

              <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/15 bg-transparent text-white/82 hover:bg-white/8 hover:text-white"
                    onClick={handleNeverShow}
                  >
                    No volver a mostrar
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-white/68 hover:bg-white/8 hover:text-white"
                    onClick={() => handleClose(true)}
                  >
                    Cerrar
                  </Button>
                </div>

                <Button
                  type="button"
                  className="w-full bg-primary text-primary-foreground shadow-[0_14px_34px_rgba(0,224,216,0.3)] hover:bg-primary/90 sm:w-auto"
                  onClick={() => void handleInstall()}
                  disabled={installButtonDisabled || installing}
                >
                  {showIosGuide ? (
                    <>
                      <Check className="h-4 w-4" aria-hidden="true" />
                      Entendido
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" aria-hidden="true" />
                      {platformInfo.platform === "ios" ? "Instalar" : "Instalar app"}
                    </>
                  )}
                </Button>
              </DialogFooter>

              {!showIosGuide && platformInfo.platform === "android" && installEvent == null ? (
                <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/65">
                  <ChevronRight className="h-4 w-4 text-primary" aria-hidden="true" />
                  El navegador todavia no expuso el instalador en esta sesion.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
